import { readMapSourceVectorLayers, type MapSourceVectorLayerRecord } from '@workspace/ui';

type SalesMapSourceLike = {
  id?: string;
  source_kind?: string;
  is_enabled?: boolean;
  vector_layers?: Array<Record<string, unknown> | string> | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type CanonicalLayerKey =
  | 'sales_locations'
  | 'sales_markets'
  | 'network_fiber'
  | 'fiber_access_points'
  | 'building_entrances';

export type SalesMapCanonicalLayerKey = CanonicalLayerKey;

export type SalesMapSourcePackageTier = 'none' | 'invalid' | 'minimum' | 'context' | 'routing';
export type SalesMapSourcePackageTone = 'muted' | 'warning' | 'success';

export type SalesMapLayerSummary = {
  layerId: CanonicalLayerKey;
  present: boolean;
  geometryExpected: 'point' | 'line' | 'polygon';
  geometryActual: string | null;
  geometryValid: boolean;
  fieldsVerified: boolean;
  missingRequiredFields: string[];
};

export type SalesMapSourcePackageSummary = {
  tier: SalesMapSourcePackageTier;
  label: string;
  tone: SalesMapSourcePackageTone;
  description: string;
  warnings: string[];
  workflowReady: boolean;
  workflowWarnings: string[];
  layers: Record<CanonicalLayerKey, SalesMapLayerSummary>;
};

export type SalesMapSourcePackageSelection<T extends SalesMapSourceLike = SalesMapSourceLike> = {
  source: T;
  summary: SalesMapSourcePackageSummary;
};

export type SalesMapSourceLayerSelection<T extends SalesMapSourceLike = SalesMapSourceLike> = {
  source: T;
  summary: SalesMapSourcePackageSummary;
  layer: MapSourceVectorLayerRecord;
  layerSummary: SalesMapLayerSummary;
};

export type SalesMapSourceCollectionSelection<T extends SalesMapSourceLike = SalesMapSourceLike> = {
  summary: SalesMapSourcePackageSummary;
  layers: Record<CanonicalLayerKey, SalesMapSourceLayerSelection<T> | null>;
};

type RequiredFieldRule = string | string[];

const REQUIRED_FIELDS: Record<CanonicalLayerKey, RequiredFieldRule[]> = {
  sales_locations: [
    'feature_id',
    'feature_identity_key',
    'display_name',
    'location_kind',
    'address_line1',
    'locality',
    'region_code',
    'postal_code',
    'serviceability_status',
    'occupancy_kind',
    'matched_place_count',
    'business_category',
  ],
  sales_markets: ['market_id', 'market_name', 'market_status'],
  network_fiber: ['feature_id', 'feature_identity_key', 'fiber_segment_id'],
  fiber_access_points: ['feature_id', 'feature_identity_key', 'access_point_id', 'access_point_kind'],
  building_entrances: [],
};

const SALES_LOCATION_WORKFLOW_FIELDS = ['occupancy_kind', 'matched_place_count', 'business_category'];

const EXPECTED_GEOMETRY: Record<CanonicalLayerKey, 'point' | 'line' | 'polygon'> = {
  sales_locations: 'point',
  sales_markets: 'polygon',
  network_fiber: 'line',
  fiber_access_points: 'point',
  building_entrances: 'point',
};

const CANONICAL_LAYER_ORDER: CanonicalLayerKey[] = [
  'sales_locations',
  'sales_markets',
  'network_fiber',
  'fiber_access_points',
  'building_entrances',
];

export const SALES_MAP_CANONICAL_LAYER_ORDER: SalesMapCanonicalLayerKey[] = [...CANONICAL_LAYER_ORDER];

const packageLabelByTier: Record<SalesMapSourcePackageTier, string> = {
  none: 'No sales package',
  invalid: 'Sales package needs fixes',
  minimum: 'Sales package: minimum',
  context: 'Sales package: context',
  routing: 'Sales package: routing',
};

const packageDescriptionByTier: Record<SalesMapSourcePackageTier, string> = {
  none: 'No canonical sales-map layers detected yet. Upload enriched sales_locations and fiber_access_points.',
  invalid:
    'Canonical sales-map layers were detected, but the package is incomplete or does not match the required enriched sales-map contract.',
  minimum:
    'The package provides an enriched sales_locations layer, but fiber_access_points is still missing.',
  context:
    'The package provides the required enriched sales_locations and fiber_access_points layers plus contextual overlays such as markets or visible fiber.',
  routing:
    'The package provides the required enriched sales_locations and fiber_access_points layers for the sales map.',
};

const packageToneByTier: Record<SalesMapSourcePackageTier, SalesMapSourcePackageTone> = {
  none: 'muted',
  invalid: 'warning',
  minimum: 'success',
  context: 'success',
  routing: 'success',
};

const TIER_RANK: Record<SalesMapSourcePackageTier, number> = {
  none: 0,
  invalid: 1,
  minimum: 2,
  context: 3,
  routing: 4,
};

const salesMapSourcePackageMetadataVersion = 2;

const normalizeKey = (value: string) => value.trim().toLowerCase();
const asObjectRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
const asBoolean = (value: unknown) => value === true;
const asString = (value: unknown) => (typeof value === 'string' ? value : '');
const asStringArray = (value: unknown) =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];

const isEnabledSource = (source: SalesMapSourceLike | null | undefined) => source?.is_enabled !== false;

const timestampRank = (source: SalesMapSourceLike) => {
  const parsed = Date.parse(source.updated_at ?? source.created_at ?? '');
  return Number.isFinite(parsed) ? parsed : 0;
};

const sourceContractHints = (source: SalesMapSourceLike | null | undefined) => {
  const contracts = new Set<string>();
  const metadata = asObjectRecord(source?.metadata);
  const append = (value: unknown) => {
    if (typeof value !== 'string') {
      return;
    }
    const normalized = normalizeKey(value);
    if (normalized) {
      contracts.add(normalized);
    }
  };

  append(metadata?.source_contract);
  for (const contract of asStringArray(metadata?.source_contracts)) {
    append(contract);
  }

  for (const layer of readMapSourceVectorLayers(source?.vector_layers ?? null)) {
    append(layer.source_contract);
    append((layer as Record<string, unknown>).contract_key);
  }

  return contracts;
};

const sourceContractAffinity = (
  source: SalesMapSourceLike | null | undefined,
  layerId: CanonicalLayerKey,
) => {
  const wanted = normalizeKey(layerId);
  const contracts = sourceContractHints(source);
  return contracts.has(wanted) ? 1000 : 0;
};

const layerContractHints = (layer: MapSourceVectorLayerRecord | null | undefined) => {
  const contracts = new Set<string>();
  const append = (value: unknown) => {
    if (typeof value !== 'string') {
      return;
    }
    const normalized = normalizeKey(value);
    if (normalized) {
      contracts.add(normalized);
    }
  };

  if (!layer) {
    return contracts;
  }

  append(layer.id);
  append((layer.raw as Record<string, unknown>).source_contract);
  append((layer.raw as Record<string, unknown>).contract_key);
  for (const contract of asStringArray((layer.raw as Record<string, unknown>).source_contracts)) {
    append(contract);
  }
  return contracts;
};

const findLayer = (
  layers: MapSourceVectorLayerRecord[],
  layerId: CanonicalLayerKey,
): MapSourceVectorLayerRecord | null => {
  const wanted = normalizeKey(layerId);
  return layers.find(layer => layerContractHints(layer).has(wanted)) ?? null;
};

const buildEmptyLayerSummary = (layerId: CanonicalLayerKey): SalesMapLayerSummary => ({
  layerId,
  present: false,
  geometryExpected: EXPECTED_GEOMETRY[layerId],
  geometryActual: null,
  geometryValid: false,
  fieldsVerified: false,
  missingRequiredFields: [],
});

const describeRequiredFieldRule = (rule: RequiredFieldRule) => (Array.isArray(rule) ? rule.join(' or ') : rule);

const listMissingFields = (layer: MapSourceVectorLayerRecord | null, requiredFields: RequiredFieldRule[]) => {
  const rawFields = layer?.fields;
  if (!rawFields || typeof rawFields !== 'object' || Object.keys(rawFields).length === 0) {
    return { fieldsVerified: false, missingRequiredFields: [] };
  }
  const fieldKeys = new Set(Object.keys(rawFields).map(normalizeKey));
  return {
    fieldsVerified: true,
    missingRequiredFields: requiredFields
      .filter(rule =>
        Array.isArray(rule)
          ? !rule.some(field => fieldKeys.has(normalizeKey(field)))
          : !fieldKeys.has(normalizeKey(rule)),
      )
      .map(describeRequiredFieldRule),
  };
};

const layerHasFields = (layer: MapSourceVectorLayerRecord | null, requiredFields: string[]) => {
  const rawFields = layer?.fields;
  if (!rawFields || typeof rawFields !== 'object' || Object.keys(rawFields).length === 0) {
    return false;
  }
  const fieldKeys = new Set(Object.keys(rawFields).map(normalizeKey));
  return requiredFields.every(field => fieldKeys.has(normalizeKey(field)));
};

export const isSalesMapLayerReady = (summary: SalesMapLayerSummary) =>
  summary.present &&
  summary.geometryValid &&
  (!summary.fieldsVerified || summary.missingRequiredFields.length === 0);

const buildLayerSummary = (
  layers: MapSourceVectorLayerRecord[],
  layerId: CanonicalLayerKey,
): SalesMapLayerSummary => {
  const layer = findLayer(layers, layerId);
  const expectedGeometry = EXPECTED_GEOMETRY[layerId];
  const geometryActual = layer?.effective_geometry ?? null;
  const geometryValid = !layer ? false : geometryActual === expectedGeometry;
  const { fieldsVerified, missingRequiredFields } = listMissingFields(layer, REQUIRED_FIELDS[layerId]);
  return {
    layerId,
    present: Boolean(layer),
    geometryExpected: expectedGeometry,
    geometryActual,
    geometryValid,
    fieldsVerified,
    missingRequiredFields,
  };
};

const appendLayerWarnings = (
  warnings: string[],
  layerId: CanonicalLayerKey,
  summary: SalesMapLayerSummary,
) => {
  if (!summary.present) {
    return;
  }
  if (!summary.geometryValid) {
    warnings.push(`${layerId} must be a ${summary.geometryExpected} layer.`);
  }
  if (summary.fieldsVerified && summary.missingRequiredFields.length > 0) {
    warnings.push(`${layerId} is missing: ${summary.missingRequiredFields.join(', ')}.`);
  }
  if (!summary.fieldsVerified && layerId !== 'building_entrances') {
    warnings.push(`${layerId} field coverage could not be verified from the current source metadata.`);
  }
};

export const summarizeSalesMapLayerIssues = (
  summary: SalesMapLayerSummary | null | undefined,
): string[] => {
  if (!summary?.present) {
    return [];
  }
  const issues: string[] = [];
  if (!summary.geometryValid) {
    issues.push(`${summary.layerId} must be a ${summary.geometryExpected} layer.`);
  }
  if (summary.fieldsVerified && summary.missingRequiredFields.length > 0) {
    issues.push(`${summary.layerId} is missing: ${summary.missingRequiredFields.join(', ')}.`);
  }
  if (!summary.fieldsVerified && summary.layerId !== 'building_entrances') {
    issues.push(`${summary.layerId} field coverage could not be verified from the current source metadata.`);
  }
  return issues;
};

export const summarizeSalesMapPackageIssues = (
  summary: SalesMapSourcePackageSummary | null | undefined,
): string[] => {
  if (!summary) {
    return [];
  }
  const issues = [...summary.warnings];
  if (summary.tier === 'invalid' && !summary.layers.sales_locations.present) {
    issues.unshift('sales_locations is required before the Sales Map can activate.');
  }
  return Array.from(new Set(issues));
};

export const summarizeSalesMapWorkflowIssues = (
  summary: SalesMapSourcePackageSummary | null | undefined,
): string[] => {
  if (!summary) {
    return [];
  }
  return Array.from(new Set(summary.workflowWarnings));
};

export const isSalesMapWorkflowReady = (summary: SalesMapSourcePackageSummary | null | undefined) =>
  summary?.workflowReady === true;

const buildPackageSummary = (
  sourceLayers: MapSourceVectorLayerRecord[],
  layers: Record<CanonicalLayerKey, SalesMapLayerSummary>,
): SalesMapSourcePackageSummary => {
  const warnings: string[] = [];

  const hasAnyCanonicalLayer = CANONICAL_LAYER_ORDER.some(layerId => layers[layerId].present);
  const locations = layers.sales_locations;
  const markets = layers.sales_markets;
  const fiber = layers.network_fiber;
  const accessPoints = layers.fiber_access_points;
  const entrances = layers.building_entrances;

  appendLayerWarnings(warnings, 'sales_locations', locations);
  appendLayerWarnings(warnings, 'sales_markets', markets);
  appendLayerWarnings(warnings, 'network_fiber', fiber);
  appendLayerWarnings(warnings, 'fiber_access_points', accessPoints);
  if (entrances.present && !entrances.geometryValid) {
    warnings.push('building_entrances should be a point layer.');
  }

  if (fiber.present && !accessPoints.present) {
    warnings.push('network_fiber is present without fiber_access_points, so the package is visible-context-only for fiber routing.');
  }
  if (hasAnyCanonicalLayer && !locations.present) {
    warnings.push('sales_locations is required before the Sales Map can activate.');
  }
  if (hasAnyCanonicalLayer && !accessPoints.present) {
    warnings.push('fiber_access_points is required before the Sales Map can activate.');
  }

  let tier: SalesMapSourcePackageTier = 'none';
  const locationsReady = isSalesMapLayerReady(locations);
  const marketsReady = isSalesMapLayerReady(markets);
  const fiberReady = isSalesMapLayerReady(fiber);
  const accessPointsReady = isSalesMapLayerReady(accessPoints);
  const entrancesReady = entrances.present && entrances.geometryValid;
  const workflowWarnings: string[] = [];
  const workflowReady =
    locationsReady &&
    accessPointsReady &&
    layerHasFields(findLayer(sourceLayers, 'sales_locations'), SALES_LOCATION_WORKFLOW_FIELDS);
  if (!workflowReady) {
    if (!accessPointsReady) {
      workflowWarnings.push(
        'fiber_access_points is required before the Sales Map can activate.',
      );
    }
    if (!layerHasFields(findLayer(sourceLayers, 'sales_locations'), SALES_LOCATION_WORKFLOW_FIELDS)) {
      workflowWarnings.push(
        'Business classification fields are missing from sales_locations. Enrich the locations GeoJSON with POI/business data before uploading it.',
      );
    }
  }

  if (!hasAnyCanonicalLayer) {
    tier = 'none';
  } else if (!locationsReady || !accessPointsReady) {
    tier = 'invalid';
  } else if (marketsReady || fiberReady || entrancesReady) {
    tier = 'context';
  } else {
    tier = 'routing';
  }

  return {
    tier,
    label: packageLabelByTier[tier],
    tone: packageToneByTier[tier],
    description: packageDescriptionByTier[tier],
    warnings,
    workflowReady,
    workflowWarnings,
    layers,
  };
};

export const findSalesMapVectorLayer = (
  source: Pick<SalesMapSourceLike, 'vector_layers'> | null | undefined,
  layerId: CanonicalLayerKey,
): MapSourceVectorLayerRecord | null => {
  const layers = readMapSourceVectorLayers(source?.vector_layers ?? null);
  return findLayer(layers, layerId);
};

export const summarizeSalesMapSourcePackage = (source: SalesMapSourceLike): SalesMapSourcePackageSummary => {
  const metadataSummary = readSalesMapSourcePackageFromMetadata(source.metadata);
  if (metadataSummary) {
    return metadataSummary;
  }
  const layers = readMapSourceVectorLayers(source.vector_layers ?? null);
  const summaries = Object.fromEntries(
    CANONICAL_LAYER_ORDER.map(layerId => [layerId, buildLayerSummary(layers, layerId)]),
  ) as Record<CanonicalLayerKey, SalesMapLayerSummary>;
  return buildPackageSummary(layers, summaries);
};

const readSalesMapSourcePackageFromMetadata = (
  metadata?: Record<string, unknown> | null,
): SalesMapSourcePackageSummary | null => {
  const packageSummary = asObjectRecord(metadata?.sales_map_source_package);
  if (!packageSummary) {
    return null;
  }
  const version = Number(packageSummary.version);
  if (!Number.isFinite(version) || version < salesMapSourcePackageMetadataVersion) {
    return null;
  }
  if (!Object.prototype.hasOwnProperty.call(packageSummary, 'workflow_ready')) {
    return null;
  }
  if (!Object.prototype.hasOwnProperty.call(packageSummary, 'workflow_warnings')) {
    return null;
  }
  const rawLayers = asObjectRecord(packageSummary.layers);
  if (!rawLayers) {
    return null;
  }
  const layers = {} as Record<CanonicalLayerKey, SalesMapLayerSummary>;
  for (const layerId of CANONICAL_LAYER_ORDER) {
    const rawLayer = asObjectRecord(rawLayers[layerId]);
    if (!rawLayer) {
      return null;
    }
    layers[layerId] = {
      layerId,
      present: asBoolean(rawLayer.present),
      geometryExpected:
        (asString(rawLayer.geometry_expected) as SalesMapLayerSummary['geometryExpected']) ||
        EXPECTED_GEOMETRY[layerId],
      geometryActual: asString(rawLayer.geometry_actual) || null,
      geometryValid: asBoolean(rawLayer.geometry_valid),
      fieldsVerified: asBoolean(rawLayer.fields_verified),
      missingRequiredFields: asStringArray(rawLayer.missing_required_fields),
    };
  }
  const tier = asString(packageSummary.tier) as SalesMapSourcePackageTier;
  if (!tier || !(tier in packageLabelByTier)) {
    return null;
  }
  return {
    tier,
    label: asString(packageSummary.label) || packageLabelByTier[tier],
    tone: (asString(packageSummary.tone) as SalesMapSourcePackageTone) || packageToneByTier[tier],
    description: asString(packageSummary.description) || packageDescriptionByTier[tier],
    warnings: asStringArray(packageSummary.warnings),
    workflowReady: asBoolean(packageSummary.workflow_ready),
    workflowWarnings: asStringArray(packageSummary.workflow_warnings),
    layers,
  };
};

const layerSelectionScore = (
  sourceSummary: SalesMapSourcePackageSummary,
  layerSummary: SalesMapLayerSummary,
) => {
  if (!layerSummary.present) {
    return -1;
  }
  let score = 1;
  if (layerSummary.geometryValid) {
    score += 10;
  }
  if (!layerSummary.fieldsVerified || layerSummary.missingRequiredFields.length === 0) {
    score += 20;
  }
  score += TIER_RANK[sourceSummary.tier] * 100;
  return score;
};

export const compareSalesMapSourcePackageTier = (
  left: SalesMapSourcePackageSummary,
  right: SalesMapSourcePackageSummary,
) => TIER_RANK[left.tier] - TIER_RANK[right.tier];

export const pickBestSalesMapSourcePackage = (
  sources: SalesMapSourceLike[],
): SalesMapSourcePackageSummary | null => {
  const enabledSources = sources.filter(isEnabledSource);
  if (!enabledSources.length) {
    return null;
  }
  return (
    enabledSources
      .map(source => summarizeSalesMapSourcePackage(source))
      .sort((left, right) => compareSalesMapSourcePackageTier(right, left))[0] ?? null
  );
};

export const pickBestSalesMapSource = <T extends SalesMapSourceLike>(
  sources: T[],
): SalesMapSourcePackageSelection<T> | null => {
  const enabledSources = sources.filter(isEnabledSource);
  if (!enabledSources.length) {
    return null;
  }
  return (
    enabledSources
      .map(source => ({ source, summary: summarizeSalesMapSourcePackage(source) }))
      .sort((left, right) => compareSalesMapSourcePackageTier(right.summary, left.summary))[0] ?? null
  );
};

export const pickBestSalesMapLayerSource = <T extends SalesMapSourceLike>(
  sources: T[],
  layerId: CanonicalLayerKey,
): SalesMapSourceLayerSelection<T> | null => {
  const candidates = sources
    .filter(isEnabledSource)
    .map(source => {
      const summary = summarizeSalesMapSourcePackage(source);
      const layerSummary = summary.layers[layerId];
      const layer = findSalesMapVectorLayer(source, layerId);
      if (!layer || !layerSummary.present) {
        return null;
      }
      return { source, summary, layer, layerSummary };
    })
    .filter((entry): entry is SalesMapSourceLayerSelection<T> => Boolean(entry));

  if (!candidates.length) {
    return null;
  }

  return candidates.sort((left, right) => {
    const rightScore = layerSelectionScore(right.summary, right.layerSummary);
    const leftScore = layerSelectionScore(left.summary, left.layerSummary);
    if (rightScore !== leftScore) {
      return rightScore - leftScore;
    }
    const rightContractAffinity = sourceContractAffinity(right.source, layerId);
    const leftContractAffinity = sourceContractAffinity(left.source, layerId);
    if (rightContractAffinity !== leftContractAffinity) {
      return rightContractAffinity - leftContractAffinity;
    }
    const rightTimestamp = timestampRank(right.source);
    const leftTimestamp = timestampRank(left.source);
    if (rightTimestamp !== leftTimestamp) {
      return rightTimestamp - leftTimestamp;
    }
    return `${right.source.id ?? ''}`.localeCompare(`${left.source.id ?? ''}`);
  })[0] ?? null;
};

export const pickBestSalesMapSourceCollection = <T extends SalesMapSourceLike>(
  sources: T[],
): SalesMapSourceCollectionSelection<T> | null => {
  const enabledSources = sources.filter(isEnabledSource);
  if (!enabledSources.length) {
    return null;
  }

  const layers = Object.fromEntries(
    CANONICAL_LAYER_ORDER.map(layerId => [layerId, pickBestSalesMapLayerSource(enabledSources, layerId)]),
  ) as Record<CanonicalLayerKey, SalesMapSourceLayerSelection<T> | null>;

  const summaryLayers = Object.fromEntries(
    CANONICAL_LAYER_ORDER.map(layerId => [layerId, layers[layerId]?.layerSummary ?? buildEmptyLayerSummary(layerId)]),
  ) as Record<CanonicalLayerKey, SalesMapLayerSummary>;

  const selectedLayers = Object.values(layers)
    .map(selection => selection?.layer ?? null)
    .filter((entry): entry is MapSourceVectorLayerRecord => Boolean(entry));

  return {
    layers,
    summary: buildPackageSummary(selectedLayers, summaryLayers),
  };
};
